import { useMemo } from 'react';
import { buildQueryFilters, TagType, TaskTagDto } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { Tag } from '@sib/ui/icons';
import { ComboboxTagOptionProps } from '@sib/ui/types';
import {
  useContactCategories,
  useContactCategoryOptions,
  useTagsAutocomplete,
  createCategoryHeader,
} from '@sib/utils';
import { TaskTagCategory } from '@sib/utils';

interface UseTaskFieldOptionsProps {
  organizationUuid?: string | null;
  debouncedSearchTerm?: string;
  defaultTagIds?: string[];
}

const filterItemsByIds = (
  items: ComboboxTagOptionProps[],
  idsToFilter: string[]
): ComboboxTagOptionProps[] =>
  items.filter(item => !idsToFilter.includes(item.value));

const filterCategoryItems = (
  categoryOptions: ComboboxTagOptionProps[],
  idsToFilter: string[]
): ComboboxTagOptionProps[] =>
  categoryOptions.map(category => {
    const { categoryItems, totalItems } = category;
    if (!categoryItems) return category;

    const filteredItems = filterItemsByIds(categoryItems, idsToFilter);
    const filteredCount = categoryItems.length - filteredItems.length;

    return {
      ...category,
      categoryItems: filteredItems,
      totalItems: Math.max(0, (totalItems ?? 0) - filteredCount),
    };
  });

export const useTaskFieldOptions = ({
  organizationUuid,
  debouncedSearchTerm = '',
  defaultTagIds = [],
}: UseTaskFieldOptionsProps) => {
  const { t } = useTranslation();

  const { options: customTagOptions, ...customTagAutocompleteProps } =
    useTagsAutocomplete({
      search: debouncedSearchTerm,
      filters: buildQueryFilters([
        {
          field: 'isPredefined',
          operator: 'eq',
          value: false,
        },
      ]),
    });

  const {
    options: predefinedTagOptions,
    totalItems: predefinedTagsTotalItems,
    sentryRef: predefinedTagsSentryRef,
    hasNextPage: predefinedTagsHasNextPage,
    isLoadingTags: predefinedTagsIsLoadingTags,
  } = useTagsAutocomplete({
    search: debouncedSearchTerm,
    filters: buildQueryFilters([
      {
        field: 'isPredefined',
        operator: 'eq',
        value: true,
      },
    ]),
  });

  const contactCategories = useContactCategories({
    organizationUuid,
    debouncedSearchTerm,
    useEmail: false,
  });

  const contactCategoryOptions = useContactCategoryOptions({
    contactCategories,
    ...(!organizationUuid && {
      categories: [TaskTagCategory.InternalEmployees],
    }),
  });

  const options = useMemo(() => {
    const filteredContactOptions = filterCategoryItems(
      contactCategoryOptions,
      defaultTagIds
    );
    const filteredPredefinedOptions = filterItemsByIds(
      predefinedTagOptions,
      defaultTagIds
    );
    const filteredCustomOptions = filterItemsByIds(
      customTagOptions,
      defaultTagIds
    );

    const predefinedFilteredCount =
      predefinedTagOptions.length - filteredPredefinedOptions.length;
    const groupedOptions = [
      ...filteredContactOptions,
      {
        ...createCategoryHeader(
          TaskTagCategory.Predefined,
          t('components.taskForm.fields.tag.predefined'),
          Tag,
          'var(--color-primary)',
          filteredPredefinedOptions,
          TagType.Custom
        ),
        totalItems: Math.max(
          0,
          predefinedTagsTotalItems - predefinedFilteredCount
        ),
        sentryRef: predefinedTagsSentryRef,
        hasNextPage: predefinedTagsHasNextPage,
        isLoading: predefinedTagsIsLoadingTags,
      },
      ...filteredCustomOptions,
    ];

    return groupedOptions;
  }, [
    contactCategoryOptions,
    customTagOptions,
    predefinedTagOptions,
    predefinedTagsTotalItems,
    predefinedTagsSentryRef,
    predefinedTagsHasNextPage,
    predefinedTagsIsLoadingTags,
    defaultTagIds,
    t,
  ]);

  return {
    options,
    customTagOptions,
    customTagAutocompleteProps,
    predefinedTagOptions,
  };
};

export const convertTaskTagsToOptions = (tags: TaskTagDto[]) =>
  tags.map(taskTag => {
    if (taskTag.type === TagType.Custom && taskTag.tag)
      return {
        value: taskTag.tag.uuid,
        label: taskTag.tag.name,
        color: taskTag.tag.color,
        field: TagType.Custom,
      };

    if (taskTag.type === TagType.Contact && taskTag.contact)
      return {
        value: taskTag.contact.id,
        label: taskTag.contact.name || taskTag.contact.email || '',
        color: 'var(--color-secondary)',
        field: TagType.Contact,
      };

    if (taskTag.type === TagType.Employee && taskTag.employee)
      return {
        value: taskTag.employee.id,
        label: taskTag.employee.name || '',
        color: 'var(--color-secondary)',
        field: TagType.Employee,
      };
  }) as ComboboxTagOptionProps[];
